(function () {
  const SELECTORS = {
    container: '[data-atom-infinite-container]',
    grid: '[data-atom-infinite-grid]',
    item: '.atom-collection__item',
    next: '[data-atom-infinite-next]',
    pagination: '[data-atom-pagination]',
    sentinel: '[data-atom-infinite-sentinel]',
    status: '[data-atom-infinite-status]',
  };

  const states = new WeakMap();
  const prefetchDistance = 900;
  const requestTimeout = 15000;

  function absoluteUrl(href, base = window.location.href) {
    const url = new URL(href, base);
    url.hash = '';
    return url.toString();
  }

  function setStatus(status, message) {
    if (!status) return;
    if (message) {
      status.textContent = message;
      status.hidden = false;
      return;
    }
    status.textContent = '';
    status.hidden = true;
  }

  function disconnect(container) {
    const state = states.get(container);
    if (!state) return;
    state.cleanup();
    states.delete(container);
  }

  function setup(container) {
    if (!container) return;

    disconnect(container);

    const grid = container.querySelector(SELECTORS.grid);
    const nextLink = container.querySelector(SELECTORS.next);
    const pagination = container.querySelector(SELECTORS.pagination);
    const sentinel = container.querySelector(SELECTORS.sentinel);
    const status = container.querySelector(SELECTORS.status);

    if (!grid || !sentinel || !('IntersectionObserver' in window)) {
      pagination?.removeAttribute('hidden');
      container.classList.remove('is-infinite-ready');
      return;
    }

    if (!nextLink) {
      container.classList.remove('is-infinite-ready');
      pagination?.removeAttribute('hidden');
      setStatus(status, '');
      return;
    }

    container.classList.add('is-infinite-ready');
    pagination?.setAttribute('hidden', '');

    let nextUrl = absoluteUrl(nextLink.href);
    const contextUrl = window.location.pathname + window.location.search;
    const loadedUrls = new Set([absoluteUrl(window.location.href)]);
    const state = {
      disposed: false,
      failed: false,
      loading: false,
      frame: 0,
      timer: 0,
      controller: null,
      observer: null,
      cleanup: null,
    };

    const isCurrent = () =>
      !state.disposed &&
      states.get(container) === state &&
      container.isConnected &&
      container.querySelector(SELECTORS.grid) === grid &&
      window.location.pathname + window.location.search === contextUrl;

    function stopWatching() {
      state.observer?.disconnect();
      window.removeEventListener('scroll', scheduleCheck);
      window.removeEventListener('resize', scheduleCheck);
      window.removeEventListener('pageshow', scheduleCheck);
      if (state.frame) window.cancelAnimationFrame(state.frame);
      state.frame = 0;
    }

    state.cleanup = () => {
      state.disposed = true;
      stopWatching();
      state.controller?.abort();
      if (state.timer) window.clearTimeout(state.timer);
      grid.removeAttribute('aria-busy');
    };

    function showFallback() {
      state.failed = true;
      stopWatching();
      container.classList.remove('is-infinite-ready');
      pagination?.removeAttribute('hidden');
      if (!pagination) nextLink.classList.remove('visually-hidden');
      setStatus(status, 'More products could not be loaded. Use the page links to continue.');
    }

    function checkPosition() {
      state.frame = 0;
      if (!isCurrent() || state.loading || state.failed || !nextUrl) return;
      if (sentinel.getBoundingClientRect().top <= window.innerHeight + prefetchDistance) loadNextPage();
    }

    function scheduleCheck() {
      if (!isCurrent() || state.failed || !nextUrl || state.frame) return;
      state.frame = window.requestAnimationFrame(checkPosition);
    }

    async function loadNextPage() {
      if (!isCurrent() || state.loading || state.failed || !nextUrl) return;
      const requestedUrl = nextUrl;
      state.loading = true;
      state.controller = new AbortController();
      state.timer = window.setTimeout(() => state.controller?.abort(), requestTimeout);
      grid.setAttribute('aria-busy', 'true');
      setStatus(status, 'Loading more products...');

      try {
        const response = await fetch(requestedUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: state.controller.signal,
        });
        if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);
        const html = await response.text();
        if (!isCurrent()) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingContainer = doc.querySelector(SELECTORS.container);
        const incomingGrid = incomingContainer?.querySelector(SELECTORS.grid);
        const incomingItems = Array.from(incomingContainer?.querySelectorAll(`${SELECTORS.grid} > ${SELECTORS.item}`) || []);
        if (!incomingGrid || !incomingItems.length || incomingGrid.dataset.id !== grid.dataset.id) {
          throw new Error('Collection page did not contain the expected product grid');
        }

        const incomingNext = incomingContainer.querySelector(SELECTORS.next);
        const incomingNextUrl = incomingNext ? absoluteUrl(incomingNext.getAttribute('href'), requestedUrl) : '';
        if (incomingNextUrl) {
          const incomingUrl = new URL(incomingNextUrl);
          if (
            incomingUrl.origin !== window.location.origin ||
            incomingUrl.pathname !== window.location.pathname ||
            incomingNextUrl === requestedUrl ||
            loadedUrls.has(incomingNextUrl)
          ) {
            throw new Error('Collection pagination did not advance');
          }
        }

        const productUrls = new Set(
          Array.from(grid.querySelectorAll('.atom-collection-card[href]'), (link) => absoluteUrl(link.href))
        );
        const fragment = document.createDocumentFragment();
        let addedCount = 0;
        incomingItems.forEach((item) => {
          const productLink = item.querySelector('.atom-collection-card[href]');
          if (!productLink) throw new Error('Collection product card is missing its link');
          const productUrl = absoluteUrl(productLink.getAttribute('href'), requestedUrl);
          if (productUrls.has(productUrl)) return;
          productUrls.add(productUrl);
          const clonedItem = document.importNode(item, true);
          clonedItem.querySelectorAll('img').forEach((image) => {
            image.loading = 'lazy';
            image.decoding = 'async';
            image.removeAttribute('fetchpriority');
          });
          fragment.appendChild(clonedItem);
          addedCount += 1;
        });
        if (!addedCount) throw new Error('Collection page did not contain any new products');

        grid.appendChild(fragment);
        loadedUrls.add(requestedUrl);
        const incomingPagination = incomingContainer.querySelector(SELECTORS.pagination);
        if (pagination && incomingPagination) pagination.innerHTML = incomingPagination.innerHTML;
        nextUrl = incomingNextUrl;
        if (nextUrl) {
          nextLink.href = nextUrl;
        } else {
          nextLink.remove();
          stopWatching();
        }
        window.AtomCollectionExtraFilters?.apply?.(true);
        setStatus(status, '');
      } catch {
        if (isCurrent()) showFallback();
      } finally {
        if (state.timer) window.clearTimeout(state.timer);
        state.timer = 0;
        state.controller = null;
        if (isCurrent()) {
          state.loading = false;
          grid.removeAttribute('aria-busy');
          scheduleCheck();
        }
      }
    }

    states.set(container, state);
    state.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) scheduleCheck();
      },
      { rootMargin: `${prefetchDistance}px 0px ${prefetchDistance}px 0px` }
    );
    state.observer.observe(sentinel);
    window.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('resize', scheduleCheck);
    window.addEventListener('pageshow', scheduleCheck);
    scheduleCheck();
  }

  function init(scope) {
    (scope || document).querySelectorAll(SELECTORS.container).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  window.addEventListener('atom:collection-grid:updated', (event) => {
    const container = event.detail?.container || document.querySelector(SELECTORS.container);
    disconnect(container);
    queueMicrotask(() => setup(container));
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
